import { useElementDimensionObserver } from '@saucelabs/sl-util-helpers';
import React, { useEffect, useMemo, useRef } from 'react';

import { actions, selectors } from '../../ducks/diffUi';
import { DiffUiDisplayType } from '../../ducks/diffUi/constants';
import { ImageType } from '../../global/constants';
import { graphql, useFragment } from '../../graphql/__generated__';
import {
  BuildFragFragment,
  DiffFragFragment,
  DiffStatus,
  DiffsOrderBy,
  RegionFragFragment,
} from '../../graphql/__generated__/graphql';
import { useVisualDispatch, useVisualSelector } from '../../reduxHooks';
import ProgressBar from '../common/ProgressBar';
import { getVisualProgressData } from '../common/utils';
import DiffHeader from './DiffHeader';
import DiffReviewButtons from './DiffReviewButtons';
import { DiffStage, DiffStageMetaFragment, DiffStageProps } from './DiffStage';

export const DiffViewSnapshotFragment = graphql(`
  fragment DiffView_Snapshot on Snapshot {
    id
    name
    testName
    suiteName
    branch
    browser
    browserVersion
    device
    operatingSystem
    operatingSystemVersion
    viewportWidth
    viewportHeight
    imageUrl
    ignoreRegions {
      ...RegionFrag
    }
  }
`);

export const DiffViewBaselineFragment = graphql(`
  fragment DiffView_Baseline on Baseline {
    id
    name
    testName
    suiteName
    browser
    browserVersion
    operatingSystem
    operatingSystemVersion
    viewportWidth
    viewportHeight
    imageUrl
    createdAt
    createdByUser {
      id
      username
    }
  }
`);

export const RegionFrag = graphql(`
  fragment RegionFrag on Region {
    x
    y
    width
    height
    name
  }
`);

export const RectFrag = graphql(`
  fragment RectFrag on Rect {
    x
    y
    width
    height
  }
`);

export const DiffViewFragment = graphql(`
  fragment DiffViewFragment on Diff {
    id
    createdAt
    status
    updatedByUser {
      id
      username
    }
    updatedAt
    diffBounds {
      ...RectFrag
    }
    diffClusters {
      ...RectFrag
    }
    baseline {
      ...DiffView_Baseline
    }
    snapshot {
      ...DiffView_Snapshot
    }
    ...DiffStageMetaFragment
  }
`);

type Props = {
  build: Exclude<BuildFragFragment, undefined | null>,
  diff: DiffFragFragment;
  onAccept: () => void;
  onReject: () => void;
  onUnapprove: () => void;
  diffOrder: DiffsOrderBy;
  setDiffsOrder: (order: DiffsOrderBy) => void;
}

const DiffView: React.FC<Props> = (props) => {
  const diffView = useFragment(DiffViewFragment, props.diff);
  const stageDiff = useFragment(DiffStageMetaFragment, props.diff);
  const baseline = useFragment(DiffViewBaselineFragment, diffView.baseline);
  const snapshot = useFragment(DiffViewSnapshotFragment, diffView.snapshot);
  const dispatch = useVisualDispatch();
  const { displayType } = useVisualSelector(selectors.getDiffUi);

  const isAccepted = diffView?.status === DiffStatus.Approved;
  const isRejected = diffView?.status === DiffStatus.Rejected;

  const progressBarData = useMemo(() => getVisualProgressData(props.build), [props.build]);

  const stageRef = useRef<HTMLDivElement>(null);
  const { elementDims } = useElementDimensionObserver(stageRef);

  useEffect(() => {
    const { width, height } = elementDims;
    dispatch(actions.setStageDims({
      // eslint-disable-next-line no-nested-ternary
      width: width > 0 ? (
        displayType === DiffUiDisplayType.SideBySide ? width / 2 : width
      ) : 1,
      height: height > 0 ? height : 1,
    }));
  }, [dispatch, displayType, elementDims]);

  const stageDims: Omit<DiffStageProps, 'type' | 'url'> = {
    diff: stageDiff,
    ignoreRegions: snapshot?.ignoreRegions as RegionFragFragment[],
  };

  return (
    <div className="relative flex flex-col h-full w-full">
      <div
        className="absolute"
        style={{ top: '-50px', right: '16px' }}
      >
        <ProgressBar
          cntClass="py-2"
          data={progressBarData}
        />
      </div>
      <DiffHeader
        build={props.build}
        diffOrder={props.diffOrder}
        diffView={diffView}
        setDiffsOrder={props.setDiffsOrder}
      />
      <div className="flex gap-2 flex-1 min-h-0 bg-gray-100 relative">
        <div className="flex flex-col flex-grow min-w-0">
          <DiffReviewButtons
            diff={diffView}
            isAccepted={isAccepted}
            isRejected={isRejected}
            onAccept={props.onAccept}
            onReject={props.onReject}
            onUnapprove={props.onUnapprove}
          />
          <div
            ref={stageRef}
            className="flex h-full flex-row w-full"
          >
            <DiffStage
              type={ImageType.BASELINE}
              url={baseline?.imageUrl}
              {...stageDims}
            />

            {displayType === DiffUiDisplayType.SideBySide && (
              // TODO: Convert into draggable separator
              <div className="h-full bg-gray-500 w-px relative z-10" />
            )}

            <DiffStage
              type={ImageType.SNAPSHOT}
              url={snapshot?.imageUrl}
              {...stageDims}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiffView;
